import { useEffect, type RefObject } from "react";

function findScrollParent(el: HTMLElement | null): Element | null {
    if (!el) return null;
    const style = getComputedStyle(el);
    if (style.overflowY === "auto" || style.overflowY === "scroll") return el;
    return findScrollParent(el.parentElement);
}

interface InfiniteScrollOptions {
    /** Whether there is another page to load. */
    hasNextPage: boolean;
    /** Whether a page is currently loading (prevents duplicate fetches). */
    isLoading: boolean;
    /** Called when the sentinel scrolls into view and a fetch should start. */
    onLoadMore: () => void;
    /** Disables the observer entirely (e.g. while a search filter is active). */
    disabled?: boolean;
}

/**
 * Observes a sentinel element and calls `onLoadMore` when it becomes visible,
 * enabling cursor-based infinite scroll inside the nearest scrollable ancestor.
 * The observer is torn down and skipped while loading, when there is no next
 * page, or when `disabled` is set.
 */
export function useInfiniteScrollOnVisible<T extends HTMLElement>(
    sentinelRef: RefObject<T | null>,
    { hasNextPage, isLoading, onLoadMore, disabled = false }: InfiniteScrollOptions,
): void {
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !hasNextPage || isLoading || disabled) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) onLoadMore();
            },
            { root: findScrollParent(sentinel.parentElement), threshold: 0.1 },
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [sentinelRef, hasNextPage, isLoading, disabled, onLoadMore]);
}
